import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";
import { Loader2, Check, Layers, Percent, Scale } from "lucide-react";
import { PLACEMENT_LABEL, RATE_KEYS } from "./adShared";

export function RatesSection({ rates, setRates }) {
  const [rateForm, setRateForm] = useState({ home_top: "", home: "", browse: "", browse_bottom: "", browse_sidebar: "", listing: "" });
  const [savingRates, setSavingRates] = useState(false);
  useEffect(() => { if (rates) setRateForm(Object.fromEntries(RATE_KEYS.map((k) => [k, rates.rates[k] ?? ""]))); }, [rates]);
  const saveRates = async () => {
    for (const k of RATE_KEYS) {
      const v = Number(rateForm[k]);
      if (!(v > 0 && v <= 1000)) return toast.error(`Enter a valid ${k} rate (0–1000)`);
    }
    setSavingRates(true);
    try {
      const { data } = await api.put("/admin/ads/rates", Object.fromEntries(RATE_KEYS.map((k) => [k, Number(rateForm[k])])));
      setRates((r) => ({ ...r, rates: data.rates }));
      toast.success("Placement rates updated");
    } catch (e) { toast.error(errText(e)); }
    finally { setSavingRates(false); }
  };
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="ad-rates-section">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><Layers size={18} className="text-[#F5551E]" /> Placement daily rates</h3>
        <p className="text-[11px] text-slate-400 mb-4">Set the CAD price per day for each banner slot. Changes apply to new bookings immediately.</p>
        {!rates ? (
          <Skeleton className="h-16 rounded-xl" />
        ) : (
          <div className="flex items-end gap-3 flex-wrap">
            {RATE_KEYS.map((k) => (
              <div key={k}>
                <Label className="text-[11px] font-semibold text-slate-500">{PLACEMENT_LABEL[k]} — $/day</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <Input type="number" min={1} max={1000} step="0.5" value={rateForm[k]} onChange={(e) => setRateForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`ad-rate-${k}-input`} className="rounded-xl h-9 w-28 pl-6" />
                </div>
              </div>
            ))}
            <Button onClick={saveRates} disabled={savingRates} data-testid="ad-rates-save-btn" className="rounded-xl h-9 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1">{savingRates ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save rates</Button>
            <span className="text-[11px] text-slate-400 ml-1">Defaults: Home top ${rates.defaults?.home_top} · Home ${rates.defaults?.home} · Browse ${rates.defaults?.browse} · Browse bottom ${rates.defaults?.browse_bottom} · Sidebar ${rates.defaults?.browse_sidebar} · Listing ${rates.defaults?.listing}</span>
          </div>
        )}
      </div>
  );
}

export function TiersSection() {
  const [tiers, setTiers] = useState(null);
  const [tierForm, setTierForm] = useState({ d14: "", d30: "" });
  const [savingTiers, setSavingTiers] = useState(false);
  useEffect(() => { api.get("/admin/ads/tiers").then((r) => { setTiers(r.data); setTierForm({ d14: r.data.d14, d30: r.data.d30 }); }).catch(() => {}); }, []);
  const saveTiers = async () => {
    const d14 = Number(tierForm.d14), d30 = Number(tierForm.d30);
    if (!(d14 >= 0 && d14 <= 90) || !(d30 >= 0 && d30 <= 90)) return toast.error("Discounts must be 0–90%");
    if (d30 < d14) return toast.error("30-day discount must be ≥ 14-day discount");
    setSavingTiers(true);
    try {
      const { data } = await api.put("/admin/ads/tiers", { d14, d30 });
      setTiers((t) => ({ ...t, d14: data.d14, d30: data.d30 }));
      toast.success("Length discounts updated");
    } catch (e) { toast.error(errText(e)); }
    finally { setSavingTiers(false); }
  };
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="ad-tiers-section">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><Percent size={18} className="text-[#F5551E]" /> Length discounts</h3>
        <p className="text-[11px] text-slate-400 mb-4">Reward longer campaigns. These % discounts apply automatically to runs of 14+ and 30+ days.</p>
        {!tiers ? (
          <Skeleton className="h-16 rounded-xl" />
        ) : (
          <div className="flex items-end gap-3 flex-wrap">
            {[["d14", "14+ days"], ["d30", "30+ days"]].map(([k, lbl]) => (
              <div key={k}>
                <Label className="text-[11px] font-semibold text-slate-500">{lbl} — % off</Label>
                <div className="relative mt-1">
                  <Input type="number" min={0} max={90} step="1" value={tierForm[k]} onChange={(e) => setTierForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`ad-tier-${k}-input`} className="rounded-xl h-9 w-28 pr-6" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>
            ))}
            <Button onClick={saveTiers} disabled={savingTiers} data-testid="ad-tiers-save-btn" className="rounded-xl h-9 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1">{savingTiers ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save discounts</Button>
            <span className="text-[11px] text-slate-400 ml-1">Defaults: 14-day {tiers.defaults?.d14}% · 30-day {tiers.defaults?.d30}%</span>
          </div>
        )}
      </div>
  );
}

export function WeightsSection({ weights, setWeights }) {
  const [weightForm, setWeightForm] = useState({ home_top: "", home: "", browse: "", browse_bottom: "", browse_sidebar: "", listing: "" });
  const [savingWeights, setSavingWeights] = useState(false);
  useEffect(() => { if (weights) setWeightForm(Object.fromEntries(RATE_KEYS.map((k) => [k, weights.weights[k] ?? ""]))); }, [weights]);
  const saveWeights = async () => {
    for (const k of RATE_KEYS) {
      const v = Number(weightForm[k]);
      if (!Number.isInteger(v) || v < 1 || v > 10) return toast.error(`${k} default weight must be an integer 1–10`);
    }
    setSavingWeights(true);
    try {
      const { data } = await api.put("/admin/ads/weights", Object.fromEntries(RATE_KEYS.map((k) => [k, Number(weightForm[k])])));
      setWeights((w) => ({ ...w, weights: data.weights }));
      toast.success("Default rotation weights updated");
    } catch (e) { toast.error(errText(e)); }
    finally { setSavingWeights(false); }
  };
  return (
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 mt-6" data-testid="ad-weights-section">
        <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2 mb-1"><Scale size={18} className="text-[#F5551E]" /> Default rotation weights</h3>
        <p className="text-[11px] text-slate-400 mb-4">The starting weight new ads inherit in each slot (higher = shown more often). Tune any individual ad's weight in the list above.</p>
        {!weights ? (
          <Skeleton className="h-16 rounded-xl" />
        ) : (
          <div className="flex items-end gap-3 flex-wrap">
            {RATE_KEYS.map((k) => (
              <div key={k}>
                <Label className="text-[11px] font-semibold text-slate-500">{PLACEMENT_LABEL[k]} — weight</Label>
                <Input type="number" min={1} max={10} step="1" value={weightForm[k]} onChange={(e) => setWeightForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`ad-weight-default-${k}-input`} className="rounded-xl h-9 w-24 mt-1" />
              </div>
            ))}
            <Button onClick={saveWeights} disabled={savingWeights} data-testid="ad-weights-save-btn" className="rounded-xl h-9 bg-[#F5551E] hover:bg-[#E04812] text-white gap-1">{savingWeights ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save weights</Button>
            <span className="text-[11px] text-slate-400 ml-1">Defaults: Home top {weights.defaults?.home_top} · Home {weights.defaults?.home} · Browse {weights.defaults?.browse} · Browse bottom {weights.defaults?.browse_bottom} · Sidebar {weights.defaults?.browse_sidebar} · Listing {weights.defaults?.listing}</span>
          </div>
        )}
      </div>
  );
}
